import React, { useState, useEffect, useRef } from "react";
import "../../vendor/vendorList.css";
import { ReactComponent as SearchIc } from "../../../assets/svg/search.svg";
import { ReactComponent as AddIc } from "../../../assets/svg/add_icon.svg";
import { ReactComponent as EditIc } from "../../../assets/svg/edit_ic.svg";
import { ReactComponent as DeleteIc } from "../../../assets/svg/delete_ic.svg";
import { ReactComponent as RarIc } from "../../../assets/svg/rar_ic.svg";
import { ReactComponent as LarIc } from "../../../assets/svg/lar_ic.svg";
import { ReactComponent as DownArrowIc } from "../../../assets/svg/down_arrow_ics.svg";
import ReactPaginate from "react-paginate";
import { Dropdown, Modal, Spinner, Toast, ToastContainer } from "react-bootstrap";
import { ReactComponent as CloseIc } from "../../../assets/svg/close-modal.svg";
import { ReactComponent as ImgPlaceholderIc } from "../../../assets/svg/add-image.svg";
import {    getBankListApi,    submitBankApi,    updateBankApi,    deleteBankApi,    getBankByIdApi} from "./bank-api.js";
import DeleteModal from "../../common/deleteModel";

const FormInput = ({
    label,
    required,
    type = "text",
    placeholder = "Enter here...",
    value,
    onChange,
    onKeyDown,
    error,
    options = [],
    containerClass = "col-6 p-0 d-flex flex-column",
    containerStyle = {},
    maxLength
}) => {
    const finalStyle = {
        gap: "6px",
        position: "relative",
        width: containerClass.includes("flex-grow-1") ? undefined : "266px",
        ...containerStyle
    };

    return (
        <div className={containerClass} style={finalStyle}>
            <label className="fs-13 fw-600 mb-0">
                {label}
                {required && <span className="text-danger ms-1">*</span>}
            </label>
            {type === "select" ? (
                <select
                    className={`form-select fs-14 border px-3 bank-modal-input ${error ? "border-danger" : ""}`}
                    style={{ borderRadius: "8px", height: "40px", borderColor: error ? "#dc3545" : "#E2E8F0" }}
                    value={value}
                    onChange={onChange}
                >
                    {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            ) : (
                <input
                    type={type}
                    className={`form-control fs-14 border px-3 bank-modal-input ${error ? "border-danger" : ""}`}
                    placeholder={placeholder}
                    style={{ borderRadius: "8px", height: "40px", borderColor: error ? "#dc3545" : "#E2E8F0" }}
                    value={value}
                    onChange={(e) => {
                        if (type === "number") {
                            const val = e.target.value;
                            if (val !== "" && Number(val) < 0) {
                                return;
                            }
                        }
                        onChange(e);
                    }}
                    onKeyDown={(e) => {
                        if (type === "number" && (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+")) {
                            e.preventDefault();
                        }
                        if (onKeyDown) {
                            onKeyDown(e);
                        }
                    }}
                    min={type === "number" ? "0" : undefined}
                    maxLength={maxLength}
                />
            )}
            <span 
                className="text-danger fs-11 d-block text-truncate" 
                style={{ 
                    position: "absolute",
                    top: "100%",
                    left: "0",
                    marginTop: "2px", 
                    lineHeight: "14px",
                    height: "14px",
                    visibility: error ? "visible" : "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    width: "100%"
                }}
                title={error ? (Array.isArray(error) ? error[0] : error) : ""}
            >
                {error ? (Array.isArray(error) ? error[0] : error) : ""}
            </span>
        </div>
    );
};

export const BankList = () => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [search, setSearch] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [banks, setBanks] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Delete confirmation states
    const [deleteId, setDeleteId] = useState(null);
    const [isOpenDelete, setIsOpenDelete] = useState(false);

    const [formData, setFormData] = useState({
        bank_name: "",
        branch_name: "",
        opening_balance: "",
        ifsc_code: "",
        swift_code: "",
        bank_code: "",
        holder_name: "",
        account_number: "",
        account_type: "CURRENT",
        image: null
    });

    const [errors, setErrors] = useState({});
    const fileInputRef = useRef(null);

    // Toast snackbar notification states
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarStatus, setSnackbarStatus] = useState(true);

    const placeholderStyle = {
        fontFamily: "'Lexend Deca', sans-serif",
        fontSize: "13px",
        fontWeight: "400",
        color: "#8790A1"
    };

    const fetchBanks = async () => {
        setLoading(true);
        try {
            const skip = (page - 1) * limit;
            const response = await getBankListApi({
                search: search,
                limit: limit,
                skip: skip
            });
            if (response && response.data && response.data.success) {
                setBanks(response.data.banks || []);
                setTotalRecords(response.data.total_rec || 0);
                setTotalPages(response.data.total_pgs !== undefined ? response.data.atotal_pgs + 1 : 0);
            } else {
                setBanks([]);
                setTotalRecords(0);
                setTotalPages(0);
            }
        } catch (error) {
            console.error("Error loading banks:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchBanks();
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [page, limit, search]);

    const resetForm = () => {
        setFormData({
            bank_name: "",
            branch_name: "",
            opening_balance: "",
            ifsc_code: "",
            swift_code: "",
            bank_code: "",
            holder_name: "",
            account_number: "",
            account_type: "CURRENT",
            image: null
        });
        setEditingId(null);
        setErrors({});
    };

    const handleLogoClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setFormData(prev => ({ ...prev, image: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };
    const handleInputChange = (field, value) => {
        let updatedValue = value;
        if (field === "ifsc_code") {
            updatedValue = value.toUpperCase().slice(0, 11);
        }
        if (field === "account_number") {
            updatedValue = value.replace(/[^0-9]/g, "");
        }
        setFormData(prev => ({ ...prev, [field]: updatedValue }));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };


const validateBankForm = (form) => {
    const newErrors = {};
    if (!form?.bank_name?.trim()) {
        newErrors.bank_name = ["Bank Name is required"];
    }
    if (!form?.branch_name?.trim()) {
        newErrors.branch_name = ["Branch Name is required"];
    }
    if (form?.opening_balance == null || form?.opening_balance === "") {
        newErrors.opening_balance = ["Opening Balance is required"];
    }
    if (!form?.ifsc_code?.trim()) {
        newErrors.ifsc_code = ["IFSC Code is required"];
    } else if (form?.ifsc_code?.trim()?.length !== 11) {
        newErrors.ifsc_code = ["IFSC Code must be 11 characters"];
    }
    if (!form?.holder_name?.trim()) {
        newErrors.holder_name = ["Account Holder Name is required"];
    }
    const accNum = form?.account_number?.toString()?.trim();
    if (!accNum) {
        newErrors.account_number = ["Account Number is required"];
    } else if (!/^\d+$/.test(accNum)) {
        newErrors.account_number = ["Only numeric values are allowed"];
    }
    return newErrors;
};

    const handleSave = async (e) => {
        e.preventDefault();

        const formErrors = validateBankForm(formData);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            setSnackbarMessage("Please correct the errors in the form.");
            setSnackbarStatus(false);
            setShowSnackbar(true);
            return;
        }

        setSaving(true);
        setErrors({});

        try {
            const payload = {
                ...formData,
                bank_name: formData.bank_name.trim(),
                branch_name: formData.branch_name.trim(),
                holder_name: formData.holder_name.trim(),
                account_number: formData.account_number.toString().trim(),
                ifsc_code: formData.ifsc_code.trim(),
                swift_code: formData.swift_code ? formData.swift_code.trim() : null,
                bank_code: formData.bank_code ? formData.bank_code.trim() : null,
            };

            let response;
            if (editingId) {
                response = await updateBankApi(editingId, payload);
            } else {
                response = await submitBankApi(payload);
            }

            if (response && response.data && response.data.success) {
                setSnackbarMessage(response.data.message || (editingId ? "Bank updated successfully!" : "Bank saved successfully!"));
                setSnackbarStatus(true);
                setShowSnackbar(true);
                setShowModal(false);
                resetForm();
                fetchBanks();
            } else if (response && response.data && response.data.error) {
                setErrors(response.data.error);
                setSnackbarMessage(response?.data?.message || "Validation failed");
                setSnackbarStatus(false);
                setShowSnackbar(true);
            } else {
                setSnackbarMessage(response?.data?.message || "An error occurred");
                setSnackbarStatus(false);
                setShowSnackbar(true);
            }
        } catch (error) {
            console.error("Error saving bank:", error);
            setSnackbarMessage("An error occurred while saving the bank.");
            setSnackbarStatus(false);
            setShowSnackbar(true);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = async (id) => {
        try {
            const response = await getBankByIdApi(id);
            if (response && response.data && response.data.success) {
                const bank = response.data.bank;
                setFormData({
                    bank_name: bank.bank_name || "",
                    branch_name: bank.branch_name || "",
                    opening_balance: bank.opening_balance || "",
                    ifsc_code: bank.ifsc_code || "",
                    swift_code: bank.swift_code || "",
                    bank_code: bank.bank_code || "",
                    holder_name: bank.holder_name || "",
                    account_number: bank.account_number || "",
                    account_type: bank.account_type || "CURRENT",
                    image: bank.image || null
                });
                setEditingId(id);
                setShowModal(true);
            }
        } catch (error) {
            console.error("Error loading bank details:", error);
        }
    };

    const handleDelete = (id) => {
        setDeleteId(id);
        setIsOpenDelete(true);
    };

    const deleteBank = async () => {
        if (deleteId === null) return;
        try {
            const response = await deleteBankApi(deleteId);
            if (response && response.data && response.data.success) {
                setSnackbarMessage(response.data.message || "Bank deleted successfully!");
                setSnackbarStatus(true);
                setShowSnackbar(true);
                fetchBanks();
            } else {
                setSnackbarMessage(response?.data?.message || "Error deleting bank");
                setSnackbarStatus(false);
                setShowSnackbar(true);
            }
        } catch (error) {
            console.error("Error deleting bank:", error);
            setSnackbarMessage("An error occurred while deleting the bank.");
            setSnackbarStatus(false);
            setShowSnackbar(true);
        } finally {
            setDeleteId(null);
            setIsOpenDelete(false);
        }
    };

    return (
        <div>
            <div className="bg-white"
                style={{
                    borderBottomRightRadius: "16px",
                    borderBottomLeftRadius: "16px",
                    width: "100%",
                    padding: "16px"
                }}>
            <style>
                {`
                    .bank-modal-input::placeholder {
                        font-family: 'Lexend Deca', sans-serif !important;
                        font-size: 13px !important;
                        font-weight: 400 !important;
                        color: #8790A1 !important;
                        opacity: 1;
                    }
                    .bank-modal-input {
                        font-family: 'Lexend Deca', sans-serif !important;
                    }
                    .bank-modal-input:focus {
                        outline: none !important;
                        box-shadow: none !important;
                        border-color: #D1D5DB !important;
                    }
                    .bank-modal-input.border-danger:focus {
                        border-color: #dc3545 !important;
                        box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2) !important;
                    }
                    .bank-modal-label {
                        color: #212129 !important;
                        font-family: 'Lexend Deca', sans-serif !important;
                        font-weight: 500 !important;
                        font-size: 13px !important;
                        line-height: 100% !important;
                    }
                `}
            </style>
            {/* Top Header */}
            <div className="d-flex align-items-center justify-content-between mb-3"
                style={{ width: "100%", height: "40px" }}>
                <p className="m-0 textGrey fs-13 fw-500">Total counts : {totalRecords}</p>
                <div className="d-flex gap-3 h-100">
                    {/* Search */}
                    <div className="search-wrapper d-flex align-items-center gap-2 px-3 py-2">
                        <SearchIc style={{ width: "18px", height: "18px", color: "#9CA3AF" }} />
                        <input
                            type="text"
                            placeholder="Search..."
                            className="border-0 outline-0 rounded-3 fs-14 fw-500 textcolor"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    {/* Add New Bank */}
                    <div className="cursor-pointer d-flex align-items-center justify-content-center gap-2 px-4 h-100"
                        style={{ backgroundColor: "#4F46E5", borderRadius: "20px", color: "white" }}
                        onClick={() => {
                            resetForm();
                            setShowModal(true);
                        }}>
                        <AddIc width={14} height={14} fill="white" />
                        <div className="fs-14 fw-700">New bank</div>
                    </div>
                </div>
            </div>

            {/* Table Header */}
            <div className="d-flex align-items-center"
                style={{ backgroundColor: "#F2F2F2", borderRadius: "4px 4px 0 0", width: "100%", height: "40px", padding: "0 16px", gap: "20px" }}>
                <div style={{ flex: "2.5" }} className="fs-13 fw-700 textcolor">Bank name</div>
                <div style={{ flex: "1.5" }} className="fs-13 fw-700 textcolor">Branch Name</div>
                <div style={{ flex: "1" }} className="fs-13 fw-700 textcolor">IFSC Code</div>
                <div style={{ flex: "1" }} className="fs-13 fw-700 textcolor">Swift Code</div>
                <div style={{ flex: "1.5" }} className="fs-13 fw-700 textcolor">Opening Bal.</div>
                <div style={{ flex: "2.5" }} className="fs-13 fw-700 textcolor">Holder Name</div>
                <div style={{ flex: "1" }} className="fs-13 fw-700 textcolor">Type</div>
            </div>

            {/* Table Body */}
            <div style={{ width: "100%" }}>
                {loading ? (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: "200px" }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : banks.length === 0 ? (
                    <div className="d-flex align-items-center justify-content-center textGrey fs-14 fw-500" style={{ height: "200px" }}>
                        No banks found.
                    </div>
                ) : (
                    banks.map((item, index) => (
                        <div
                            key={item.id}
                            className="d-flex align-items-center position-relative"
                            style={{
                                backgroundColor: hoveredIndex === index ? "#FAFAFA" : "#FFFFFF",
                                cursor: "pointer",
                                width: "100%",
                                height: "64px",
                                padding: "14px 16px",
                                borderBottom: "0.6px solid #D4DDE7",
                                gap: "20px"
                            }}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                        >
                            {/* Bank Name */}
                            <div style={{ flex: "2.5", minWidth: 0 }} className="d-flex align-items-center gap-3">
                                {item.image ? (
                                    <img src={item.image} alt="logo" style={{ width: "32px", height: "32px", borderRadius: "4px", objectFit: "cover" }} />
                                ) : (
                                    <div className="rounded d-flex align-items-center justify-content-center bg-light text-secondary fw-bold flex-shrink-0" style={{ width: "32px", height: "32px", fontSize: "14px" }}>
                                        {item.bank_name ? item.bank_name.charAt(0) : "B"}
                                    </div>
                                )}
                                <div style={{ overflow: "hidden", minWidth: 0 }}>
                                    <p className="m-0 fs-13 fw-600 textcolor" title={item.bank_name} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>{item.bank_name}</p>
                                    <p className="m-0 fs-11 textGrey" title={item.bank_code} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>Code : <span className="textPurple">{item.bank_code || "N/A"}</span></p>
                                </div>
                            </div>

                            {/* Branch */}
                            <div style={{ flex: "1.5", minWidth: 0, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }} className="fs-13 fw-500 textcolor" title={item.branch_name}>{item.branch_name}</div>

                            {/* IFSC */}
                            <div style={{ flex: "1", minWidth: 0, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }} className="fs-13 fw-500 textcolor" title={item.ifsc_code}>{item.ifsc_code}</div>

                            {/* Swift */}
                            <div style={{ flex: "1", minWidth: 0, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }} className="fs-13 fw-500 textcolor" title={item.swift_code || "-"}>{item.swift_code || "-"}</div>

                            {/* Balance */}
                            <div style={{ flex: "1.5", minWidth: 0, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }} className="fs-13 fw-500 textcolor" title={item.opening_balance ? `₹${Number(item.opening_balance).toLocaleString('en-IN')}` : '₹0'}>
                                ₹{item.opening_balance ? Number(item.opening_balance).toLocaleString('en-IN') : '0'}
                            </div>

                            {/* Holder Name */}
                            <div style={{ flex: "2.5", minWidth: 0 }} className="d-flex align-items-center gap-2">
                                <div className="rounded-circle d-flex align-items-center justify-content-center textPurple flex-shrink-0"
                                    style={{ width: "28px", height: "28px", backgroundColor: "#EEF2FF", fontSize: "12px", fontWeight: "600" }}>
                                    {item.holder_name ? item.holder_name.charAt(0) : "H"}
                                </div>
                                <div style={{ overflow: "hidden", minWidth: 0 }}>
                                    <p className="m-0 fs-13 fw-600 textcolor" title={item.holder_name} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>{item.holder_name}</p>
                                    <p className="m-0 fs-11 textGrey" title={item.account_number} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>{item.account_number}</p>
                                </div>
                            </div>

                            {/* Type */}
                            <div style={{ flex: "1" }} className="">
                                <span className="px-2 py-1 rounded-pill fs-10 fw-700"
                                    style={{
                                        backgroundColor: item.account_type === "CURRENT" ? "#DCFCE7" : "#FFEDD5",
                                        color: item.account_type === "CURRENT" ? "#166534" : "#9A3412"
                                    }}>
                                    {item.account_type}
                                </span>
                            </div>

                            {/* Action Icons on Hover */}
                            {hoveredIndex === index && (
                                <div className="d-flex gap-2"
                                    style={{
                                        position: "absolute",
                                        right: "20px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 10
                                    }}>
                                    {/* Edit Button */}
                                    <div className="rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm border cursor-pointer hover-effect"
                                        style={{ width: "32px", height: "32px", borderColor: "#E2E8F0" }}
                                        onClick={() => handleEdit(item.id)}>
                                        <EditIc width={16} height={16} stroke="#1E293B" />
                                    </div>
                                    {/* Delete Button */}
                                    <div className="rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm border cursor-pointer hover-effect"
                                        style={{ width: "32px", height: "32px", borderColor: "#E2E8F0" }}
                                        onClick={() => handleDelete(item.id)}>
                                        <DeleteIc width={16} height={16} />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Pagination Footer */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3">
                <Dropdown>
                    <span className="pagination-color fs-13 fw-500">
                        Records per page: &nbsp;
                    </span>
                    <Dropdown.Toggle
                        id="dropdown-basic"
                        className="bg-transparent pagination-color border-0 custom-dropdown-toggle p-0"
                    >
                        <span className="bg-white textcolor no-of-rows rounded-3 px-2 py-2">
                            {limit} <DownArrowIc />
                        </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                        className="my-2"
                        style={{ minWidth: "unset", width: "40%" }}
                    >
                        {[10, 25, 50, 75, 100].map((size) => (
                            <Dropdown.Item
                                key={size}
                                onClick={() => {
                                    setLimit(size);
                                    setPage(1);
                                }}
                                active={limit === size}
                                className="text-center"
                            >
                                {size}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>

                <div className="d-flex align-items-center gap-2">
                    <ReactPaginate
                        previousLabel={
                            <div
                                className="rounded-5 bg-white d-flex align-items-center justify-content-center"
                                style={{
                                    width: "32px",
                                    height: "32px",
                                }}
                            >
                                <LarIc />
                            </div>
                        }
                        nextLabel={
                            <div
                                className="rounded-5 bg-white d-flex align-items-center justify-content-center"
                                style={{
                                    width: "32px",
                                    height: "32px",
                                }}
                            >
                                <RarIc />
                            </div>
                        }
                        breakLabel={"..."}
                        pageCount={totalPages}
                        marginPagesDisplayed={2}
                        pageRangeDisplayed={3}
                        onPageChange={(event) => setPage(event.selected + 1)}
                        forcePage={Math.max(0, page - 1)}
                        containerClassName={"pagination"}
                        activeClassName={"active"}
                    />
                </div>
            </div>
        </div>

            {/* New/Edit Bank Modal */}
            <Modal
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                    resetForm();
                }}
                centered
                contentClassName="border-0"
                dialogClassName="modal-600w"
            >
                <style>
                    {`
                        .modal-600w {
                            max-width: 600px;
                            width: 620px;
                        }
                        .modal-content {
                            border-radius: 24px !important;
                            overflow: hidden !important;
                            border: none !important;
                        }
                    `}
                </style>
                <Modal.Body className="p-0" style={{
                    borderRadius: "16px",
                    width: "100%",
                    backgroundColor: "#FFFFFF",
                }}>
                    <div className="d-flex flex-column" style={{ padding: "24px", gap: "14px" }}>
                        <div className="d-flex justify-content-between align-items-center" style={{ width: "100%", height: "40px", gap: "12px", borderTopLeftRadius: "14px", borderTopRightRadius: "14px", marginBottom: "4px" }}>
                            <h4 className="m-0 fw-700 fs-22">{editingId ? "Update Bank" : "New Bank"}</h4>
                            <div className="cursor-pointer d-flex align-items-center justify-content-center border" style={{ width: "28px", height: "28px", borderRadius: "50%", borderColor: "#E2E8F0" }} onClick={() => {
                                setShowModal(false);
                                resetForm();
                            }}>
                                <CloseIc width={12} height={12} />
                            </div>
                        </div>

                        <div className="d-flex flex-column" style={{ gap: "20px" }}>
                            <div className="d-flex gap-4">
                                {/* Logo Upload Section */}
                                <div className="position-relative d-flex flex-column align-items-center cursor-pointer" style={{ width: "64px", height: "64px" }} onClick={handleLogoClick}>
                                    {formData.image ? (
                                        <img src={formData.image} alt="logo" className="rounded-circle" style={{ width: "64px", height: "64px", objectFit: "cover" }} />
                                    ) : (
                                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px", backgroundColor: "#F3F4F6" }}>
                                            <ImgPlaceholderIc width={32} height={32} />
                                        </div>
                                    )}
                                    <div className="position-absolute text-white d-flex align-items-center justify-content-center fw-600 cursor-pointer"
                                        style={{ width: "66px", height: "20px", borderRadius: "100px", fontSize: "10px", bottom: "0px", backgroundColor: "#4F46E5" }}>
                                        {formData.image ? "Change" : "+ Logo"}
                                    </div>
                                    <input type="file" ref={fileInputRef} accept="image/*" className="d-none" onChange={handleFileChange} />
                                </div>

                                {/* Bank Name */}
                                <FormInput
                                    label="Bank Name"
                                    required
                                    value={formData.bank_name}
                                    onChange={(e) => handleInputChange("bank_name", e.target.value)}
                                    error={errors.bank_name}
                                    containerClass="flex-grow-1 d-flex flex-column position-relative"
                                    containerStyle={{ gap: "8px" }}
                                />
                            </div>

                            {/* Form Grid Section */}
                            <div className="d-flex flex-column" style={{ gap: "20px", width: "552px" }}>
                                <div className="row m-0" style={{ gap: "20px" }}>
                                    <FormInput
                                        label="Branch Name"
                                        required
                                        value={formData.branch_name}
                                        onChange={(e) => handleInputChange("branch_name", e.target.value)}
                                        error={errors.branch_name}
                                    />
                                    <FormInput
                                        label="Opening Balance"
                                        required
                                        type="number"
                                        value={formData.opening_balance}
                                        onChange={(e) => handleInputChange("opening_balance", e.target.value)}
                                        error={errors.opening_balance}
                                    />
                                </div>
                                <div className="row m-0" style={{ gap: "20px" }}>
                                    <FormInput
                                        label="IFSC Code"
                                        required
                                        value={formData.ifsc_code}
                                        onChange={(e) => handleInputChange("ifsc_code", e.target.value.toUpperCase().slice(0, 11))}
                                        error={errors.ifsc_code}
                                        maxLength={11}
                                    />
                                    <FormInput
                                        label="Swift Code"
                                        value={formData.swift_code}
                                        onChange={(e) => handleInputChange("swift_code", e.target.value)}
                                        error={errors.swift_code}
                                    />
                                </div>
                                <div className="row m-0" style={{ gap: "20px" }}>
                                    <FormInput
                                        label="Bank Code"
                                        value={formData.bank_code}
                                        onChange={(e) => handleInputChange("bank_code", e.target.value)}
                                        error={errors.bank_code}
                                    />
                                    <FormInput
                                        label="Account Holder Name"
                                        required
                                        value={formData.holder_name}
                                        onChange={(e) => handleInputChange("holder_name", e.target.value)}
                                        error={errors.holder_name}
                                    />
                                </div>
                                <div className="row m-0" style={{ gap: "20px" }}>
                                    <FormInput
                                        label="Account Number"
                                        required
                                        value={formData.account_number}
                                        onChange={(e) => handleInputChange("account_number", e.target.value)}
                                        onKeyDown={(e) => {
                                            if (
                                                !/^[0-9]$/.test(e.key) &&
                                                !["Backspace", "Tab", "Delete", "ArrowLeft", "ArrowRight", "Enter"].includes(e.key) &&
                                                !e.ctrlKey &&
                                                !e.metaKey
                                            ) {
                                                e.preventDefault();
                                            }
                                        }}
                                        error={errors.account_number}
                                        maxLength={30}
                                    />
                                    <FormInput
                                        label="Account Type"
                                        required
                                        type="select"
                                        value={formData.account_type}
                                        onChange={(e) => handleInputChange("account_type", e.target.value)}
                                        error={errors.account_type}
                                        options={[
                                            { label: "CURRENT", value: "CURRENT" },
                                            { label: "SAVING", value: "SAVING" }
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-3 mt-auto">
                            <button
                                className="btn border px-4 py-2 fw-600 fs-14"
                                style={{ borderRadius: "8px", backgroundColor: "white", minWidth: "120px" }}
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary px-4 py-2 fw-600 fs-14"
                                style={{ borderRadius: "8px", backgroundColor: "#4F46E5", border: "none", minWidth: "120px" }}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? <Spinner animation="border" size="sm" className="me-2" /> : (editingId ? "Update" : "Save")}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Delete confirmation modal */}
            <DeleteModal
                title="Bank"
                isOpen={isOpenDelete}
                onClose={() => setIsOpenDelete(false)}
                onDelete={deleteBank}
            />

            {/* Snackbar feedback notification */}
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
                <Toast className="d-inline-block m-1" onClose={() => setShowSnackbar(false)} show={showSnackbar} delay={3000} autohide bg={snackbarStatus ? "success" : "danger"}>
                    <Toast.Body className="text-white fw-500">{snackbarMessage}</Toast.Body>
                </Toast>
            </ToastContainer>
        </div>
    );
};

